import { useQueryClient } from "@tanstack/react-query";
import { Alert, Skeleton, buttonClass, cx, errorMessage, useDocumentTitle } from "@unizpay/ui";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { resetMode } from "../lib/mode";
import { takeReturnTo } from "../lib/returnTo";
import { AuthShell } from "./LoginPage";

export function LoginCallbackPage() {
  useDocumentTitle("Signing in");
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(token ? null : "This sign-in link is missing its token.");
  // The token is single-use: guard against React StrictMode running the effect twice.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api.auth
      .verify(token)
      .then(() => {
        queryClient.clear();
        resetMode();
        // E.g. back to an invitation the user opened before signing in.
        navigate(takeReturnTo() ?? "/dashboard", { replace: true });
      })
      .catch((err: unknown) => setError(errorMessage(err)));
  }, [token, navigate, queryClient]);

  return (
    <AuthShell>
      {error ? (
        <>
          <h1 className="text-lg font-semibold">Could not sign you in</h1>
          <Alert tone="danger" className="mt-4">
            {error}
          </Alert>
          <Link to="/login" className={cx(buttonClass("primary", "lg"), "mt-6 w-full")}>
            Request a new link
          </Link>
        </>
      ) : (
        <output className="block" aria-label="Signing you in">
          <h1 className="text-lg font-semibold">Signing you in…</h1>
          <Skeleton className="mt-4 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-2/3" />
        </output>
      )}
    </AuthShell>
  );
}
